/**
 * Access-review task page — V98-5-2 (above the per-row task from Track 3).
 *
 *   what it covers · due · the period(s) it counts in (the overdue chip is
 *                  the page header's — not repeated here, V98-5 review T5-H12)
 *   the path       Review · <reviewer> › Attest · <reviewer> › Approve · per row › Close · auto
 *   close rule     "Closes when every system is attested and every row approved"
 *                  (the server refuses Complete otherwise — 409 unattested)
 *   systems        per-system progress from the SAME rows query as the task
 *                  below (T5-B5: one rows query): a two-segment meter with a
 *                  legend whose order the meta line follows; "N to review"
 *                  filters the rows below to that system; Attest… for the
 *                  assigned reviewer once every row in it is decided
 *   response       a late review is a known deviation: Start / Write the
 *                  management response (V98-5-7)
 *
 * Reads: GET /access-reviews/:id/progress + /rows (any member). Writes: attest
 * = the assigned reviewer only (server-enforced, no role override); start
 * response = its owner, the program lead or owner/admin.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Textarea } from '@aigovops/design-system';
import type { AccessReviewSystem } from '@aigovops/core';
import { attestAccessReviewSystem, getAccessReviewProgress, startManagementResponse } from '../../lib/cert-ops-api';
import { accessReviewRows } from '../../lib/coverage-api';
import { fmtDate } from '../../lib/cert-ops-model';
import { PROGRAM_LABELS } from '../../lib/nav-config';
import { firstName, nameOf, type Names } from '../../lib/today-model';
import { ActionDialog, Chip, domId, errorText } from '../today/shared';
import '../../pages/cert-ops.css';

const STATUS: Record<AccessReviewSystem['status'], { text: string; tone: 'ok' | 'neutral' }> = {
  not_started: { text: 'Not started', tone: 'neutral' }, in_progress: { text: 'In progress', tone: 'neutral' }, reviewed: { text: 'Reviewed · to attest', tone: 'neutral' },
  attested: { text: 'Attested', tone: 'ok' }, approved: { text: 'Approved', tone: 'ok' },
};

type SystemCounts = { total: number; decided: number; approved: number; removed: number };

export function AccessReviewProgressPanel({ slug, prefix, reviewId, me, names, onDone, onFilter }: {
  slug: string; prefix: string; reviewId: string; me: string; names: Names; onDone: (msg: string, focusId: string) => void;
  /** T5-H12: "N to review" filters the rows below to that system. */
  onFilter?: (system: string) => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['access-review-progress', slug, reviewId], queryFn: () => getAccessReviewProgress(slug, reviewId), retry: false });
  // T5-B5: the counts come from the same rows the task lists (one query, shared cache key).
  const rows = useQuery({ queryKey: ['access-review-rows', slug, reviewId], queryFn: () => accessReviewRows(slug, reviewId), retry: false });
  const [attesting, setAttesting] = useState<AccessReviewSystem | null>(null);
  const [err, setErr] = useState<string | null>(null);
  if (q.isLoading) return <p className="gp-dim">Loading progress…</p>;
  if (q.error || !q.data) return <Alert variant="error">The review’s progress could not load.</Alert>;
  const p = q.data.progress;
  const reviewer = p.reviewer_user_ids[0] ?? null;
  const isReviewer = !!reviewer && p.reviewer_user_ids.includes(me);
  const who = (u: string | null) => (u === 'auto' ? 'auto' : u ? (u === me ? 'you' : firstName(nameOf(names, u))) : 'per row');
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['access-review-progress', slug, reviewId] }), qc.invalidateQueries({ queryKey: ['access-review-rows', slug, reviewId] }), qc.invalidateQueries({ queryKey: ['today', slug] })]);
  const startResponse = async () => {
    if (!p.response.deviation_key || !p.response.program) return;
    setErr(null);
    try { const r = await startManagementResponse(slug, p.response.program, p.response.deviation_key); navigate(`${prefix}/responses/${encodeURIComponent(r.response.work_item.work_item_id)}?from=review:${encodeURIComponent(reviewId)}`); }
    catch (e) { setErr(errorText(e, 'The response could not be started.')); }
  };
  const counts = new Map<string, SystemCounts>();
  if (rows.data) {
    for (const r of rows.data.rows) {
      const c = counts.get(r.system_key) ?? { total: 0, decided: 0, approved: 0, removed: 0 };
      c.total++; if (r.decision) c.decided++; if (r.approval) c.approved++; if (r.decision?.decision === 'revoke') c.removed++;
      counts.set(r.system_key, c);
    }
  }
  const countsOf = (s: AccessReviewSystem): SystemCounts => counts.get(s.system_key) ?? { total: s.rows_total, decided: s.decided, approved: s.approved, removed: s.revoked };
  const totalRows = rows.data?.total ?? p.totals.rows_total;
  const step = p.steps.find((s) => !s.done)?.step ?? null;
  return (
    <section className="gp-hat-block" aria-labelledby="ar-progress-h" data-part="access-review-progress">
      <h2 id="ar-progress-h">{`Systems · ${p.totals.attested} of ${p.totals.systems} attested · ${totalRows} accounts`}</h2>
      {p.occurrence ? (
        <p className="gp-ow-meta">
          {p.occurrence.covers ? <span>{`Covers ${fmtDate(p.occurrence.covers.start)} – ${fmtDate(p.occurrence.covers.end)}`}</span> : null}
          <span>{p.late && p.status === 'completed' ? `Done ${p.days_late} day${p.days_late === 1 ? '' : 's'} late` : `Due ${fmtDate(p.occurrence.due_at)}`}</span>
          {p.occurrence.periods.length ? <span>{`Counts in ${p.occurrence.periods.map((x) => `${x.label}${x.program ? ` (${PROGRAM_LABELS[x.program as keyof typeof PROGRAM_LABELS] ?? x.program})` : ''}`).join(', ')}`}</span> : null}
        </p>
      ) : null}
      <ol className="gp-path" aria-label="Path to close">
        {p.steps.map((s) => (
          <li key={s.step} aria-current={s.step === step ? 'step' : undefined}>
            {`${s.step === 'review' ? 'Review' : s.step === 'attest' ? 'Attest' : s.step === 'approve' ? 'Approve' : 'Close'} · ${who(s.step === 'approve' ? null : s.who)}${s.done ? ' ✓' : ''}`}
          </li>
        ))}
      </ol>
      <p className="gp-dim" data-part="ar-close-rule">Closes when every system is attested and every row approved.</p>
      <p className="gp-meter-legend" aria-hidden="true">
        <span><i className="gp-meter-done" />Approved</span><span><i className="gp-meter-part" />Reviewed</span><span><i className="gp-meter-rest" />To review</span>
      </p>
      {err ? <Alert variant="error">{err}</Alert> : null}
      <ul className="gp-owed-list" aria-label="Systems">
        {p.systems.map((s) => {
          const c = countsOf(s);
          const reviewed = c.decided - c.approved;
          const left = c.total - c.decided;
          return (
            <li key={s.system_key} className="gp-ow" data-system={s.system_key} id={domId('ar-sys', s.system_key)} tabIndex={-1}>
              <span className="gp-ow-main">
                <span className="gp-ow-line"><span className="gp-ow-t">{s.system_key}</span><Chip text={STATUS[s.status].text} tone={STATUS[s.status].tone} /></span>
                <span className="gp-meter" role="img" aria-label={`${c.approved} approved, ${reviewed} reviewed, ${left} to review, of ${c.total} accounts`}>
                  {c.approved ? <span className="gp-meter-done" style={{ flexGrow: c.approved }} /> : null}
                  {reviewed > 0 ? <span className="gp-meter-part" style={{ flexGrow: reviewed }} /> : null}
                  {left > 0 ? <span className="gp-meter-rest" style={{ flexGrow: left }} /> : null}
                </span>
                <span className="gp-ow-meta">
                  <span>{`${c.approved} approved · ${reviewed} reviewed · ${left} to review${c.removed ? ` · ${c.removed} to remove` : ''}`}</span>
                  {s.attestation ? <span>{`Attested by ${who(s.attestation.attested_by_user_id)} · ${fmtDate(s.attestation.attested_at)}`}</span> : null}
                </span>
              </span>
              <span className="gp-ow-act">
                {s.ready_to_attest && isReviewer
                  ? <Button compact onClick={() => setAttesting(s)} aria-label={`Attest · ${s.system_key}`}>Attest…</Button>
                  : s.ready_to_attest ? <span className="gp-dim">{`${who(reviewer)} attests`}</span>
                    : !s.attestation && left > 0 && onFilter
                      ? <Button compact variant="ghost" onClick={() => onFilter(s.system_key)} aria-label={`Show the ${left} to review · ${s.system_key}`}>{`${left} to review`}</Button>
                      : null}
              </span>
            </li>
          );
        })}
      </ul>
      {p.response.needed ? (
        <div className="gp-state gp-state--warn" data-part="ar-response">
          <strong>Known deviation · management response</strong>
          <span className="gp-ow-meta">
            <Chip text={p.response.state === 'not_started' ? 'Not started' : p.response.state === 'open' ? 'Drafting' : 'Submitted'} tone={p.response.state === 'submitted' ? 'ok' : 'neutral'} />
            {p.response.work_item_id
              ? <Link to={`${prefix}/responses/${encodeURIComponent(p.response.work_item_id)}?from=review:${encodeURIComponent(reviewId)}`}>{p.response.state === 'submitted' ? 'Open the response' : 'Write response'}</Link>
              : p.response.program
                ? <Button compact onClick={() => void startResponse()}>Write response</Button>
                : <span className="gp-ow-meta"><Button compact aria-disabled aria-describedby="ar-no-program">Write response</Button><span id="ar-no-program" className="gp-dim">No program to count it in yet</span></span>}
          </span>
        </div>
      ) : null}
      <AttestDialog slug={slug} reviewId={reviewId} system={attesting} onClose={() => setAttesting(null)}
        onDone={async (m, s) => { setAttesting(null); await refresh(); onDone(m, domId('ar-sys', s)); }} />
    </section>
  );
}

function AttestDialog({ slug, reviewId, system, onClose, onDone }: {
  slug: string; reviewId: string; system: AccessReviewSystem | null; onClose: () => void; onDone: (m: string, system: string) => Promise<void>;
}) {
  const standard = system ? `I reviewed every account in ${system.system_key} (${system.rows_total}) and recorded keep or revoke for each.` : '';
  const [text, setText] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // T5-H18: the standard wording is the prefilled value (editable), never placeholder-only text.
  const value = text ?? standard;
  return (
    <ActionDialog open={!!system} title={system ? `Attest · ${system.system_key}` : 'Attest'} submitLabel="Attest" busy={busy} error={err}
      onClose={() => { setErr(null); setText(null); onClose(); }}
      onSubmit={async () => {
        if (!system) return false;
        setBusy(true); setErr(null);
        try { await attestAccessReviewSystem(slug, reviewId, system.system_key, value.trim() || undefined); setText(null); await onDone(`${system.system_key} attested`, system.system_key); return true; }
        catch (e) { setErr(errorText(e, 'The attestation could not be recorded.')); return false; }
        finally { setBusy(false); }
      }}>
      {system ? <p className="gp-dim">{`${system.rows_total} accounts · ${system.revoked} to remove · recorded with your name and the time`}</p> : null}
      <Field id="attest-text" label="Your statement" hint="The standard wording; edit it if you need to">
        <Textarea rows={3} maxLength={2000} value={value} onChange={(e) => setText(e.currentTarget.value)} />
      </Field>
    </ActionDialog>
  );
}
